import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Movie, formatYear, formatRuntime, formatDirector, StreamingData, movieApi } from '../services/api';
import { posterService, MoviePoster } from '../services/posterApi';

interface MovieDetailsProps {
  movie: Movie;
  showFullDetails?: boolean;
  className?: string;
}

const MovieDetails: React.FC<MovieDetailsProps> = ({ 
  movie, 
  showFullDetails = true,
  className = "" 
}) => {
  const navigate = useNavigate();
  const hasRatings = movie.imdb_rating || movie.rotten_tomatoes || movie.metacritic;
  const [streamingData, setStreamingData] = useState<StreamingData | null>(null);
  const [isLoadingStreaming, setIsLoadingStreaming] = useState(false);
  const [poster, setPoster] = useState<MoviePoster | null>(null);
  const [isLoadingPoster, setIsLoadingPoster] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);

  useEffect(() => {
    if (showFullDetails) {
      loadStreamingData();
    }
    loadPoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie.id, showFullDetails]);

  const getRatingUrl = (ratingType: 'imdb' | 'rotten_tomatoes' | 'metacritic') => {
    switch (ratingType) {
      case 'imdb':
        return movie.imdb_id ? `https://www.imdb.com/title/${movie.imdb_id}` : `https://www.imdb.com/find?q=${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}`;
      case 'rotten_tomatoes':
        return `https://www.rottentomatoes.com/search?search=${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}`;
      case 'metacritic':
        return `https://www.metacritic.com/search/movie/${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}/results`;
      default:
        return '';
    }
  };

  const handleGenreClick = (genre: string) => {
    navigate(`/browse?tab=genres&genre=${encodeURIComponent(genre)}`);
  };

  const handleDirectorClick = (director: string) => {
    navigate(`/browse?tab=directors&director=${encodeURIComponent(director)}`);
  };

  const handleActorClick = (actor: string) => {
    navigate(`/browse?tab=actors&actor=${encodeURIComponent(actor)}`);
  };

  const loadPoster = async () => {
    setIsLoadingPoster(true);
    
    try {
      const posterData = await posterService.getMoviePoster(movie.id);
      setPoster(posterData);
    } catch (error) {
      console.error('Failed to load poster:', error);
      setPoster(null);
    } finally {
      setIsLoadingPoster(false);
    }
  };

  const loadStreamingData = async () => {
    setIsLoadingStreaming(true);
    
    try {
      const data = await movieApi.getMovieStreaming(movie.id);
      setStreamingData(data);
    } catch (error) {
      console.error('Failed to load streaming data:', error);
      setStreamingData(null);
    } finally {
      setIsLoadingStreaming(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
        <h1 className="text-3xl font-bold mb-2">{movie.omdb_title || movie.cleaned_title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-blue-100">
          {movie.year && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              {formatYear(movie.year)}
            </span>
          )}
          
          {movie.director && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              Dir: <button 
                onClick={() => handleDirectorClick(movie.director!)} 
                className="text-white hover:text-gray-200 hover:underline cursor-pointer"
              >
                {formatDirector(movie.director)}
              </button>
            </span>
          )}
          
          {movie.runtime && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              {formatRuntime(movie.runtime)}
            </span>
          )}
          
          {movie.rated && (
            <span className="px-2 py-1 bg-blue-500 bg-opacity-50 rounded text-sm font-medium">
              {movie.rated}
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Movie Poster and Basic Info Layout */}
        <div className="flex flex-col lg:flex-row gap-6 mb-6">
          {/* Poster Section */}
          <div className="flex-shrink-0">
            <div 
              className="relative w-64 h-96 bg-gray-200 rounded-lg overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => poster?.posterUrl && setShowPosterModal(true)}
            >
              {isLoadingPoster ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Loading poster...</p>
                  </div>
                </div>
              ) : poster?.posterUrl ? (
                <img
                  src={poster.posterUrl}
                  alt={`${movie.omdb_title || movie.cleaned_title} poster`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://via.placeholder.com/300x450/374151/f9fafb?text=${encodeURIComponent('No Poster')}`;
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm">No Poster</p>
                  </div>
                </div>
              )}
              {poster?.source && (
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 text-white text-xs rounded">
                  {poster.source.toUpperCase()}
                </div>
              )}
            </div>
          </div>

          {/* Details Section */}
          <div className="flex-1">
        {/* Ratings Section */}
        {hasRatings && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Ratings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {movie.imdb_rating && (
                <a
                  href={getRatingUrl('imdb')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-yellow-50 p-4 rounded-lg text-center block hover:bg-yellow-100 transition-colors cursor-pointer hover:shadow-md relative"
                >
                  <svg className="w-4 h-4 absolute top-2 right-2 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <div className="text-2xl font-bold text-yellow-600 mb-1">
                    {movie.imdb_rating}
                  </div>
                  <div className="text-sm text-yellow-700 font-medium">IMDb</div>
                </a>
              )}
              
              {movie.rotten_tomatoes && (
                <a
                  href={getRatingUrl('rotten_tomatoes')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-red-50 p-4 rounded-lg text-center block hover:bg-red-100 transition-colors cursor-pointer hover:shadow-md relative"
                >
                  <svg className="w-4 h-4 absolute top-2 right-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <div className="text-2xl font-bold text-red-600 mb-1">
                    {movie.rotten_tomatoes}
                  </div>
                  <div className="text-sm text-red-700 font-medium">Rotten Tomatoes</div>
                </a>
              )}
              
              {movie.metacritic && (
                <a
                  href={getRatingUrl('metacritic')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-50 p-4 rounded-lg text-center block hover:bg-green-100 transition-colors cursor-pointer hover:shadow-md relative"
                >
                  <svg className="w-4 h-4 absolute top-2 right-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <div className="text-2xl font-bold text-green-600 mb-1">
                    {movie.metacritic}
                  </div>
                  <div className="text-sm text-green-700 font-medium">Metacritic</div>
                </a>
              )}
            </div>
          </div>
        )}

            {/* Genres */}
            {movie.genres && movie.genres.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Genres</h2>
                <div className="flex flex-wrap gap-2">
                  {movie.genres.map((genre, index) => (
                    <button
                      key={index}
                      onClick={() => handleGenreClick(genre)}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actors */}
            {movie.actors && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Cast</h2>
                <div className="flex flex-wrap gap-2">
                  {movie.actors.split(', ').map((actor, index) => (
                    <button
                      key={index}
                      onClick={() => handleActorClick(actor)}
                      className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full hover:bg-green-200 transition-colors cursor-pointer"
                    >
                      {actor}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Plot */}
            {movie.plot && movie.plot !== "NO MATCH FOUND" && showFullDetails && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Plot</h2>
                <p className="text-gray-700 leading-relaxed">{movie.plot}</p>
              </div>
            )}

            {/* Additional Details */}
            {showFullDetails && (
              <div className="mb-6 space-y-4">
                {movie.box_office && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-900">Box Office</span>
                    <span className="text-gray-600">{movie.box_office}</span>
                  </div>
                )}
                
                {movie.awards && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="font-medium text-gray-900">Awards</span>
                    <span className="text-gray-600">{movie.awards}</span>
                  </div>
                )}
                
                {movie.imdb_id && (
                  <div className="flex justify-between py-2">
                    <span className="font-medium text-gray-900">IMDb ID</span>
                    <a 
                      href={`https://www.imdb.com/title/${movie.imdb_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {movie.imdb_id}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Streaming Availability */}
        {showFullDetails && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Where to Watch</h2>
            {isLoadingStreaming ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-gray-600">Loading streaming options...</span>
              </div>
            ) : streamingData && streamingData.streaming_options.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {streamingData.streaming_options.map((option, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${
                      option.type === 'subscription' 
                        ? 'border-green-200 bg-green-50' 
                        : option.type === 'rent'
                        ? 'border-yellow-200 bg-yellow-50'
                        : 'border-blue-200 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`font-semibold ${
                        option.type === 'subscription' 
                          ? 'text-green-800' 
                          : option.type === 'rent'
                          ? 'text-yellow-800'
                          : 'text-blue-800'
                      }`}>
                        {option.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        option.type === 'subscription' 
                          ? 'bg-green-200 text-green-800' 
                          : option.type === 'rent'
                          ? 'bg-yellow-200 text-yellow-800'
                          : 'bg-blue-200 text-blue-800'
                      }`}>
                        {option.type.charAt(0).toUpperCase() + option.type.slice(1)}
                      </span>
                    </div>
                    <div className="text-sm space-y-1">
                      {option.quality && (
                        <div className="text-gray-600">Quality: {option.quality.toUpperCase()}</div>
                      )}
                      {option.price && (
                        <div className="font-medium text-gray-900">Price: {option.price}</div>
                      )}
                      {option.link && (
                        <a
                          href={option.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded text-white transition-colors ${
                            option.type === 'subscription' 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : option.type === 'rent'
                              ? 'bg-yellow-600 hover:bg-yellow-700'
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          Watch Now →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="text-center mb-4">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-600 mb-4">No streaming options available</p>
                  <p className="text-sm text-gray-500 mb-4">Search for this movie on popular streaming platforms:</p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <a
                    href={`https://www.netflix.com/search?q=${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5.398 0v.006c3.028 8.556 5.37 15.175 8.348 23.596 2.344.058 4.85.398 4.854.398-2.8-7.924-5.923-16.747-8.487-24zm8.489 0v9.63L18.6 22.951c-.043-7.86-.004-15.913.002-22.95zM5.398 1.05V24c2.8-.057 4.848-.398 4.854-.398C8.431 17.51 6.696 9.29 5.398 1.05z"/>
                    </svg>
                    Netflix
                  </a>
                  
                  <a
                    href={`https://www.hulu.com/search?q=${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.828 10.828l-4.828 4.828L2.172 10.828A4 4 0 0 1 8 4h8a4 4 0 0 1 5.828 6.828l-4.828 4.828a4 4 0 0 1-5.172 0z"/>
                    </svg>
                    Hulu
                  </a>
                  
                  <a
                    href={`https://www.amazon.com/s?k=${encodeURIComponent(movie.omdb_title || movie.cleaned_title)}&i=instant-video`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M.045 18.02c9.284 6.923 18.111 7.459 23.135 1.238.417-.516-.073-1.26-.736-.79-3.609 2.555-8.677 3.397-14.296 1.775-5.736-1.658-9.651-5.14-11.187-8.365-.319-.669-1.003-.025-.916.544.362 2.36 1.676 4.59 4 5.598zm-.014-4.046c.328.328.513.513.513.87 0 .233-.143.414-.352.414-.296 0-.54-.181-.54-.479 0-.359.185-.544.379-.805z"/>
                    </svg>
                    Prime Video
                  </a>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Similarity Score (if provided) */}
        {movie.similarity_score && (
          <div className="mt-6 p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-medium text-purple-900">Similarity Score</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-purple-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(movie.similarity_score * 100)}%` }}
                  ></div>
                </div>
                <span className="text-purple-700 font-semibold">
                  {(movie.similarity_score * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Poster Modal */}
      {showPosterModal && poster?.posterUrl && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setShowPosterModal(false)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={poster.posterUrl}
              alt={`${movie.omdb_title || movie.cleaned_title} poster`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowPosterModal(false)}
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovieDetails;
